import { createRouter, createWebHistory } from 'vue-router'
import { trackPageView } from '../services/analytics'
import { getCookieConsent } from '../services/analytics'
import { saveReferralCode, clearReferralCode } from '../utils/referral-storage'
import { logger } from '../utils/logger'
import { baseRoutes, adminRoutes } from './routes'
import { 
  supportedLocales, 
  getLocaleFromPath, 
  removeLocaleFromPath,
  detectBrowserLocale,
  isAdminPath,
  getI18nLocaleFromUrlLocale
} from './utils'
import i18n from '../i18n'

// 支持的語言列表
const locales = supportedLocales // ['en', 'zh', 'ja']

// 為每個基礎路由創建帶語言前綴的路由
function createLocalizedRoutes() {
  const localizedRoutes = []
  
  // 為每個語言創建路由
  locales.forEach(locale => {
    baseRoutes.forEach(route => {
      // 根據原始路徑創建帶語言前綴的路徑
      let localizedPath
      if (route.path === '/') {
        localizedPath = `/${locale}`
      } else {
        localizedPath = `/${locale}${route.path}`
      }
      
      localizedRoutes.push({
        path: localizedPath,
        name: route.name ? `${route.name}_${locale}` : undefined,
        component: route.component,
        meta: {
          ...route.meta,
          locale: locale,
          baseName: route.name // 保存原始路由名稱，方便導航
        }
      })
    })
  })
  
  return localizedRoutes
}

// 創建所有路由
const routes = [
  // 根路徑重定向 - 會根據瀏覽器語言重定向到對應語言
  {
    path: '/',
    redirect: (to) => {
      // 檢測瀏覽器語言或使用默認語言
      const locale = detectBrowserLocale()
      return `/${locale}`
    }
  },
  
  // 帶語言前綴的路由
  ...createLocalizedRoutes(),
  
  // 管理後台路由（不需要語言前綴）
  ...adminRoutes,
  
  // 捕獲所有未匹配的路由，重定向到對應語言的首頁
  {
    path: '/:pathMatch(.*)*',
    redirect: (to) => {
      const locale = detectBrowserLocale()
      return `/${locale}`
    }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// 路由守衛：處理語言切換和 i18n 同步
router.beforeEach(async (to, from, next) => {
  logger.log(`🛣️ [Router] beforeEach: ${from.path} -> ${to.path}`)
  
  // 管理後台路由處理（不需要語言前綴）
  if (to.meta?.isAdmin || isAdminPath(to.path)) {
    // 管理後台身份驗證
    if (to.meta?.requiresAuth) {
      const authenticated = sessionStorage.getItem('admin_authenticated')
      const loginTime = sessionStorage.getItem('admin_login_time')
      
      if (!authenticated || !loginTime) {
        logger.log('🔒 [Router] 未登錄，跳轉到登錄頁')
        next({ name: 'AdminLogin' })
        return
      }
      
      // 檢查登錄是否超過 8 小時
      const now = Date.now()
      const loginTimestamp = parseInt(loginTime)
      const hoursSinceLogin = (now - loginTimestamp) / (1000 * 60 * 60)
      
      if (hoursSinceLogin > 8) {
        logger.log('🔒 [Router] 登錄已過期，跳轉到登錄頁')
        sessionStorage.removeItem('admin_authenticated')
        sessionStorage.removeItem('admin_login_time')
        next({ name: 'AdminLogin' })
        return
      }
    } else if (to.name === 'AdminLogin') {
      // 如果已經登錄，跳轉到管理後台
      const authenticated = sessionStorage.getItem('admin_authenticated')
      if (authenticated) {
        next({ name: 'AdminDashboard' })
        return
      }
    }
    
    // 管理後台路由繼續，不需要語言處理
    next()
    return
  }
  
  // 提取語言代碼
  const locale = getLocaleFromPath(to.path)
  
  // 如果路徑沒有語言前綴，重定向到帶語言前綴的路徑
  if (!locale) {
    const detectedLocale = detectBrowserLocale()
    const pathWithoutLocale = removeLocaleFromPath(to.path)
    const newPath = pathWithoutLocale === '/' 
      ? `/${detectedLocale}` 
      : `/${detectedLocale}${pathWithoutLocale}`
    
    // 保留查詢參數和 hash
    const newRoute = {
      path: newPath,
      query: to.query,
      hash: to.hash
    }
    
    logger.log(`🌐 [Router] 重定向到帶語言前綴的路徑: ${to.path} -> ${newPath}`)
    next(newRoute)
    return
  }
  
  // 同步 i18n locale
  const i18nLocale = getI18nLocaleFromUrlLocale(locale)
  
  // 在 Composition API 模式下安全地訪問和設置 locale
  if (i18n && i18n.global && i18n.global.locale) {
    const currentLocale = i18n.global.locale.value
    if (currentLocale !== i18nLocale) {
      logger.log(`🌐 [Router] 切換 i18n locale: ${currentLocale} -> ${i18nLocale}`)
      i18n.global.locale.value = i18nLocale
      // 同時保存到 localStorage
      localStorage.setItem('locale', i18nLocale)
    }
  }
  
  // 清除推薦碼（URL 參數 ?clearRef=true）
  if (to.query.clearRef === 'true') {
    logger.log('🗑️ [Router] 檢測到清除推薦碼請求')
    
    const cleared = clearReferralCode()
    if (cleared) {
      logger.log('✅ [Router] 推薦碼已清除')
    } else {
      logger.warn('⚠️ [Router] 推薦碼清除失敗')
    }
    
    // 重定向到當前語言的首頁
    next({ 
      path: `/${locale}`, 
      query: { cleared: 'true' }
    })
    return
  }
  
  // 檢測推薦碼（URL 參數 ?ref=）
  if (to.query.ref && typeof to.query.ref === 'string') {
    const referralCode = to.query.ref.toUpperCase()
    logger.log('🎁 [Router] 檢測到推薦碼')
    
    const saved = saveReferralCode(referralCode)
    if (saved) {
      logger.log('✅ [Router] 推薦碼已保存')
    } else {
      logger.warn('⚠️ [Router] 推薦碼保存失敗')
    }
  }
  
  // 防止重複導航到相同路由
  if (to.path === from.path && to.name === from.name) {
    logger.warn('⚠️ [Router] 嘗試導航到相同路由，已阻止')
    next(false)
    return
  }
  
  // 確保 meta 對象存在
  if (!to.meta) {
    to.meta = {}
  }
  
  // 設置路由動畫
  if (!to.meta.transition && from.name && to.name) {
    const routes = ['Home', 'Invest', 'Dashboard', 'InvestSuccess', 'Whitepaper']
    const fromBaseName = from.name.split('_')[0] // 移除語言後綴
    const toBaseName = to.name.split('_')[0]
    const fromIndex = routes.indexOf(fromBaseName)
    const toIndex = routes.indexOf(toBaseName)
    
    if (toIndex > fromIndex) {
      to.meta.transition = 'slide-left'
    } else if (toIndex < fromIndex) {
      to.meta.transition = 'slide-right'
    } else {
      to.meta.transition = 'fade'
    }
  }
  
  logger.log('🛣️ [Router] 路由 meta:', to.meta)
  next()
})

// 頁面瀏覽追蹤
router.afterEach((to, from) => {
  logger.log(`✅ [Router] afterEach: ${from.path} -> ${to.path}`)
  
  const consent = getCookieConsent()
  if (consent === 'all') {
    trackPageView(to.path, to.name || '')
  }
})

export default router
